'use client'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { X } from 'lucide-react'
import { useMediaQuery } from '@/hooks/use-media-query'
import { CustomDrawerModalProps } from '@/utils/interfaces'

const CustomDrawerModal = ({
  title,
  children,
  isHeader,
  maxWidth,
  maxHeight,
  open,
  onOpenChange,
  titleClassName,
  dialogClassName = 'p-6 pt-4',
  dialogContentClassName = '',
  outsideClickDisabled = false,
  showCloseButton = true,
}: CustomDrawerModalProps) => {
  const { value: isDesktop } = useMediaQuery('(min-width: 768px)')

return (
  <div>
    {!isDesktop ? (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        repositionInputs={false}
        disablePreventScroll
        {...(outsideClickDisabled && {
          dismissible: false,
          closeThreshold: 9999,
        })}
      >
        <DrawerContent
          onInteractOutside={(e) => {
            if (outsideClickDisabled) {
              e.preventDefault()
            }
          }}
          className={`handle_remove ${dialogContentClassName}`}
          style={{
            maxWidth: maxWidth ? maxWidth : '',
            maxHeight: maxHeight ?? '',
          }}
        >
          {(isHeader || title) && (
            <DrawerHeader className="p- flex gap-3 items-center justify-between border-b border-solid border-gray-100">
              {!!title && (
                <DrawerTitle
                  className={`text-body-lg md:text-heading-md font-bold ${titleClassName}`}
                >
                  {title}
                </DrawerTitle>
              )}
              <DrawerClose>
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <X size={18} className="text-gray-800" />
                </div>
              </DrawerClose>
            </DrawerHeader>
          )}
          <div className={`${dialogClassName}`}>{children}</div>
        </DrawerContent>
      </Drawer>
    ) : (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={showCloseButton}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (outsideClickDisabled) {
              e.preventDefault()
            }
          }}
          onEscapeKeyDown={(e) => {
            outsideClickDisabled ? e.preventDefault() : null // This prevents the modal from closing by pressing esc key
          }}
          className={`p-0 gap-0 modal ${dialogContentClassName}`}
          style={{ maxWidth: maxWidth ? maxWidth : '' }}
        >
          {isHeader || title ? (
            <DialogHeader className="p-6 border-b border-solid border-gray-100">
              <DialogTitle
                className={`text-body-lg md:text-heading-md font-bold ${titleClassName}`}
              >
                {title}
              </DialogTitle>
            </DialogHeader>
          ) : (
            <VisuallyHidden.Root>
              <DialogTitle>Dialog</DialogTitle>
            </VisuallyHidden.Root>
          )}
          <div className={dialogClassName}>{children}</div>
        </DialogContent>
      </Dialog>
    )}
  </div>
)
}

export default CustomDrawerModal
